import { PaletteMode } from "@mui/material";
import { atomWithStorage } from "jotai/utils";
import { StorageKeys } from "./storage";

// Alias for allowed theme palette modes.
export type ThemeMode = PaletteMode | "system";

// Union type for supported transport perspectives: operational (arcs, default) or logical (entities).
export type TransportPerspective = "operational" | "logical";
export type UserLocalNavigationPreferences = {
	/** The mode: operational (arcs, default) or logical (entities) */
	transportsPerspective: TransportPerspective;
};

/**
 * Defines user preferences stored locally.
 */
export type UserLocalPreferences = {
	/** The selected theme mode */
	theme: ThemeMode;

	/** The selected language */
	language?: string;

	/** The browser language */
	browserLanguage?: string;

	/** The system theme mode */
	systemTheme?: PaletteMode;

	/** The last-visited URL */
	lastVisitedUrl?: string;

	/** The user's navigation preferences */
	navigationPreferences: UserLocalNavigationPreferences;
};

/**
 * Holds the user's local preferences:
 * - theme mode
 *   - dark
 *   - light
 *   - system
 * - system theme
 * - current language
 * - browser language
 * - navigation preferences
 */
export const userLocalPreferences = atomWithStorage<UserLocalPreferences>(
	StorageKeys.Preferences,
	{
		theme: "system",
		navigationPreferences: {
			transportsPerspective: "operational",
		},
	},
	undefined,
	// Read the stored value on first render, avoiding a flash of the default value (and duplicated requests).
	{ getOnInit: true }
);

/**
 * getLocalPreferences is a utility function used to access user preferences
 * in read-only mode without the need to use atoms.
 *
 * This is helpful in all contexts where hooks like useAtom are not supported.
 *
 * @returns user local preferences
 */
export const getLocalPreferences = (): UserLocalPreferences => {
	const raw = localStorage.getItem(StorageKeys.Preferences);
	return raw ? JSON.parse(raw) : { theme: "system" };
};

/**
 * setLocalPreferences is a utility function used to access user preferences
 * in write-only mode without the need to use atoms.
 *
 * This is helpful in all contexts where hooks like useAtom are not supported.
 *
 * @returns user local preferences
 */
export const setLocalPreferences = (preferences: UserLocalPreferences) => {
	localStorage.setItem(StorageKeys.Preferences, JSON.stringify(preferences));
};
